import React, { useState, useRef, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade, Navigation, Pagination } from "swiper";
import { useSelector } from "react-redux";
import { useWindowWidth } from "@react-hook/window-size";
import Hls from "hls.js";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Translate from "../../../../Components/Multilanguage/Translate";
import BannerUtil from "../util.jsx";
import { imageUrlCheck } from "../../../../utils/utils";
import { getPlayerToken } from "../../../../network/service";
import "../../scss/_yowBanner.scss";

const TrailerVideoPlayer = ({ src, isMuted, onEnded, onError }) => {
  const vRef = useRef(null);
  const hlsRef = useRef(null);

  useEffect(() => {
    const video = vRef.current;
    if (!video) return;
    video.muted = isMuted;
  }, [isMuted]);

  useEffect(() => {
    const video = vRef.current;
    if (!video || !src) return;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    video.muted = isMuted;
    video.defaultMuted = isMuted;

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: false,
        lowLatencyMode: false,
        backBufferLength: 30,
      });

      hlsRef.current = hls;

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src);
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch((e) => {
            if (e.name !== "AbortError") {
              console.log("HLS play error:", e);
            }
          });
        }
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.warn("HLS fatal error in TrailerVideoPlayer:", data.type, data.details, data.response?.code);
          if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
          }
          if (onError) onError(data);
        }
      });

      hls.attachMedia(video);
    } else if (video.canPlayType && video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((e) => {
          if (e.name !== "AbortError") {
            console.log("Native HLS play error:", e);
            if (onError) onError(e);
          }
        });
      }
    } else {
      video.src = src;
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((e) => {
          if (e.name !== "AbortError") {
            console.log("Video play error:", e);
            if (onError) onError(e);
          }
        });
      }
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src]);

  return (
    <video
      ref={vRef}
      className="trailerVideo"
      autoPlay
      muted={isMuted}
      playsInline
      referrerPolicy="no-referrer"
      onEnded={onEnded}
      onError={(e) => {
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
        if (onError) onError(e);
      }}
    />
  );
};

const YowBanner = ({ data }) => {
  const nextRef = useRef(null);
  const swiperRef = useRef(null);
  const staticTimerRef = useRef(null);
  const fallbackTimerRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [showTrailer, setShowTrailer] = useState(false);
  const [currentTrailerUrl, setCurrentTrailerUrl] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const [watchlistStatus, setWatchlistStatus] = useState({});

  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const sessionId = useSelector((state) => state?.sessionId?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);

  const windowSize = useWindowWidth();
  const isRTL = useSelector((state) => state?.isRTL?.value);
  const { playClickHandler, replaceImage, handleWatchlistToggle } = BannerUtil();

  const shows = data?.shows || [];

  const appInfo = {
    projectDetails: projectInfo,
    accessToken,
    sessionId,
  };

  useEffect(() => {
    if (shows && shows.length > 0) {
      const initialMap = {};
      shows.forEach((item) => {
        if (item?.show_id) {
          initialMap[item.show_id] = item?.watchlist_flag ?? 0;
        }
      });
      setWatchlistStatus(initialMap);
    }
  }, [shows]);

  const getImageSrc = (item) => {
    const bannerImg = windowSize > 768
      ? (item?.banner || item?.logo)
      : (item?.banner_portrait || item?.banner || item?.logo);
    if (!bannerImg) return "";
    return imageUrlCheck(bannerImg) ? bannerImg : `${process.env.REACT_APP_IMAGE_URL}${bannerImg}`;
  };

  const handleNextSlide = () => {
    if (swiperRef.current) {
      if (swiperRef.current.isEnd || activeIndex >= shows.length - 1) {
        swiperRef.current.slideTo(0);
      } else {
        swiperRef.current.slideNext();
      }
    }
  };

  const handleNextBtnClick = (e) => {
    if (e) e.stopPropagation();
    handleNextSlide();
  };

  // 1. Show static banner image for 3s if teaser works -> 2. Play trailer video -> 3. Immediately skip to next slide if teaser is missing or broken
  useEffect(() => {
    let isCancelled = false;

    if (staticTimerRef.current) clearTimeout(staticTimerRef.current);
    if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);

    setShowTrailer(false);
    setCurrentTrailerUrl(null);

    const activeItem = shows[activeIndex];
    if (!activeItem) return;

    const pId = projectInfo?.projectConfig?.pubid || projectInfo?.pubid || 50187;
    const isYowTv = Number(pId) === 50187;

    const resolveTeaserUrl = async () => {
      const videoId = activeItem?.video_time;
      if (videoId) {
        try {
          const playerTokenResponse = await getPlayerToken(appInfo, videoId);
          if (playerTokenResponse?.status === 200 && playerTokenResponse?.data?.data) {
            return (
              process.env.REACT_APP_API_URL +
              "playlistV2/playlist.m3u8?id=" +
              videoId +
              "&token=" +
              playerTokenResponse?.data?.data +
              "&type=trailer&pubid=" +
              pId
            );
          }
        } catch (err) {
          console.error("Error getting player token for banner teaser:", err);
        }
      }
      return null;
    };

    resolveTeaserUrl()
      .then((resolvedUrl) => {
        if (isCancelled) return;

        if (resolvedUrl) {
          // Teaser URL resolved successfully -> Show static banner for 3s first, then start trailer
          staticTimerRef.current = setTimeout(() => {
            if (!isCancelled) {
              setCurrentTrailerUrl(resolvedUrl);
              setShowTrailer(true);
            }
          }, 3000);
        } else {
          // Teaser token or URL failed -> Immediately skip to next slider
          handleNextSlide();
        }
      })
      .catch(() => {
        if (!isCancelled) {
          // Teaser error -> Immediately skip to next slider
          handleNextSlide();
        }
      });

    return () => {
      isCancelled = true;
      if (staticTimerRef.current) clearTimeout(staticTimerRef.current);
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    };
  }, [activeIndex, shows]);

  const handleTrailerEnded = () => {
    // Move to next item after trailer finishes
    handleNextSlide();
  };

  const handleVideoError = () => {
    // On video error, immediately skip to next slider
    setShowTrailer(false);
    setCurrentTrailerUrl(null);
    handleNextSlide();
  };

  return (
    <div className={`yowBanner ${isRTL ? "rtl" : ""} ${showTrailer && currentTrailerUrl ? "has-active-trailer" : ""}`} role="region" aria-label="Yow Banner">
      <Swiper
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        slidesPerView={1}
        effect={"fade"}
        spaceBetween={0}
        loop={false}
        modules={[EffectFade, Pagination]}
        pagination={{ clickable: true }}
        className="mySwiper"
      >
        {shows.map((item, index) => {
          const isCurrentActive = index === activeIndex;
          const activeTrailer = isCurrentActive && showTrailer && currentTrailerUrl;
          const isInWatchlist = watchlistStatus[item?.show_id] === 1;

          return (
            <SwiperSlide key={index} aria-label={`Slide ${index + 1}`}>
              <div className="bgImageContainer">
                {activeTrailer ? (
                  <TrailerVideoPlayer
                    src={currentTrailerUrl}
                    isMuted={isMuted}
                    onEnded={handleTrailerEnded}
                    onError={handleVideoError}
                  />
                ) : (
                  <img
                    src={getImageSrc(item)}
                    alt={item?.show_name ? `${item.show_name} banner` : "Banner"}
                    onError={(e) => replaceImage(e)}
                  />
                )}
              </div>

              <div className="contents" aria-label="Show Metadata">
                {item?.categories?.[0]?.category_name && (
                  <div className="categoryName">
                    {item.categories[0].category_name}
                  </div>
                )}

                <h1 className="title">
                  {item?.show_name || item?.title}
                </h1>

                {item?.synopsis && (
                  <div className="descriptionContainer">
                    <p className="description" aria-label="Show Synopsis">
                      {(() => {
                        const text = item.synopsis;
                        if (text.toLowerCase().startsWith("pause/rewind:")) {
                          return (
                            <>
                              <strong>Pause/Rewind:</strong>{text.slice(13)}
                            </>
                          );
                        }
                        return (
                          <>
                            <strong>Pause/Rewind:</strong> {text}
                          </>
                        );
                      })()}
                    </p>
                    {(item?.duration_text) && (
                      <p className="directorText">
                        {item?.duration_text}
                      </p>
                    )}
                  </div>
                )}

                <div className="buttonContainer">
                  <button
                    className="watchNow"
                    onClick={() => playClickHandler(item)}
                    aria-label={`Play ${item?.show_name}`}
                  >
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    <span><Translate textKey={"play"} /></span>
                  </button>

                  <button
                    className="watchlistButton"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item?.show_id) {
                        handleWatchlistToggle(item.show_id, watchlistStatus, setWatchlistStatus);
                      }
                    }}
                    aria-label={isInWatchlist ? `Remove ${item?.show_name} from watchlist` : `Add ${item?.show_name} to watchlist`}
                  >
                    {isInWatchlist ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span>WATCHLISTED</span>
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M2.5 8H13.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M8 2.5V13.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>WATCHLIST</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {/* Navigation control buttons for YowBanner */}
      <div className="gaia-controls">
        <button
          ref={nextRef}
          className="gaia-next"
          onClick={handleNextBtnClick}
          aria-label="Next slide"
        >
          <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="44" height="44" fill="#4D4C4B" />
            <path d="M19.5 15.75L25.75 22L19.5 28.25" stroke="#F6F6F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* Mute Toggle Button: Direct child of .yowBanner, right bottom corner above pagination */}
      <button
        className="muteToggleButton"
        onClick={(e) => {
          e.stopPropagation();
          setIsMuted((prev) => !prev);
        }}
        aria-label={isMuted ? "Unmute trailer video" : "Mute trailer video"}
        title={isMuted ? "Unmute sound" : "Mute sound"}
      >
        {isMuted ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.5 3.75L19.5 20.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M7.5 8.25V15.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M18 10.019C18.4825 10.5669 18.7487 11.2718 18.7487 12.0019C18.7487 12.7319 18.4825 13.4368 18 13.9847" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M14.25 14.475V21L7.5 15.75H3C2.80109 15.75 2.61032 15.671 2.46967 15.5303C2.32902 15.3897 2.25 15.1989 2.25 15V8.99999C2.25 8.80108 2.32902 8.61031 2.46967 8.46966C2.61032 8.32901 2.80109 8.24999 3 8.24999H7.5L8.13937 7.75311" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10.5142 5.90531L14.2501 3V10.0153" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20.7817 7.5C21.8886 8.73755 22.5006 10.3397 22.5006 12C22.5006 13.6603 21.8886 15.2624 20.7817 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14.25 3V21L7.5 15.75H3C2.80109 15.75 2.61032 15.671 2.46967 15.5303C2.32902 15.3897 2.25 15.1989 2.25 15V8.99999C2.25 8.80108 2.32902 8.61031 2.46967 8.46966C2.61032 8.32901 2.80109 8.24999 3 8.24999H7.5L14.25 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M18 10.019C18.4825 10.5669 18.7487 11.2718 18.7487 12.0019C18.7487 12.7319 18.4825 13.4368 18 13.9847" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20.7817 7.5C21.8886 8.73755 22.5006 10.3397 22.5006 12C22.5006 13.6603 21.8886 15.2624 20.7817 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
    </div>
  );
};

export default YowBanner;
